import type { ChatMessageMetadata } from "@/types/chat";
import type { UIMessage } from "ai";
import { cleanLiveRequestMessage } from "@/lib/live-chat";

// Shared between the client-side persistence hook (use-message-persistence.ts)
// and the server-side persistence path (src/app/api/chat/server-persistence.ts)
// so the two writers can never drift on row shape — isomorphic, no "use
// client", no supabase import.

function isCompactionTextPart(part: UIMessage["parts"][number]): boolean {
  return (
    part.type === "text" &&
    (part.providerMetadata?.anthropic?.type === "compaction" ||
      part.providerMetadata?.openai?.type === "compaction")
  );
}

export function getTextContent(message: UIMessage): string {
  return message.parts
    .filter((p) => p.type === "text" && !isCompactionTextPart(p))
    .map((p) => (p as { type: "text"; text: string }).text)
    .join("");
}

export function serializeParts(message: UIMessage): unknown[] | null {
  // Store structured parts when needed for attachments/tools or provider
  // compaction metadata that must be sent back on later turns.
  // Empty compaction parts (streaming artifacts) are dropped — replaying one
  // is rejected by Anthropic ("compaction.content: content cannot be empty").
  const parts = message.parts.filter(
    (p) => !(isCompactionTextPart(p) && (p as { text: string }).text.trim() === "")
  );
  const hasNonText = parts.some((p) => p.type !== "text");
  const hasCompaction = parts.some(isCompactionTextPart);
  if (!hasNonText && !hasCompaction) return null;
  return parts as unknown[];
}

function sumTokenUsageBreakdown(
  breakdown: NonNullable<ChatMessageMetadata["tokenUsageBreakdown"]>
) {
  return breakdown.reduce(
    (total, item) => ({
      input: total.input + item.input,
      output: total.output + item.output,
      cache_read: total.cache_read + item.cache_read,
      cache_write: total.cache_write + item.cache_write,
    }),
    { input: 0, output: 0, cache_read: 0, cache_write: 0 }
  );
}

/**
 * Shared by both message tables: builds the `token_usage` JSONB blob plus
 * the top-level `cost` column from an assistant message's metadata. `cost`
 * itself is computed upstream, once, in the chat route's `messageMetadata`
 * callback (`sumCost(tokenUsageBreakdown, modelPricing)`) from whatever
 * pricing was in effect for this turn — this function just reads it through,
 * it never recomputes it from a fresh `global_models.pricing` read. That
 * means a later price change never alters an already-persisted message's
 * cost, and the same number the client saw during streaming is what gets
 * saved.
 */
function buildTokenUsageAndCost(message: UIMessage) {
  const metadata = (message as UIMessage & { metadata?: ChatMessageMetadata }).metadata;
  const breakdown = message.role === "assistant" ? (metadata?.tokenUsageBreakdown ?? null) : null;
  const fallbackTotal = breakdown && breakdown.length > 0 ? sumTokenUsageBreakdown(breakdown) : null;
  const total =
    fallbackTotal && metadata
      ? {
          input: metadata.inputTokens ?? fallbackTotal.input,
          output: metadata.outputTokens ?? fallbackTotal.output,
          cache_read: metadata.cacheReadTokens ?? fallbackTotal.cache_read,
          cache_write: metadata.cacheWriteTokens ?? fallbackTotal.cache_write,
        }
      : fallbackTotal;
  const tokenUsage =
    breakdown && breakdown.length > 0
      ? { breakdown, total, pricing: metadata?.pricingSnapshot ?? null }
      : null;
  const cost = message.role === "assistant" ? (metadata?.cost ?? null) : null;
  // Per-use server-side tool cost (web search / code execution) — billed
  // separately from tokens, see toolCost's doc comment in types/chat.ts.
  const toolCost = message.role === "assistant" ? (metadata?.toolCost ?? null) : null;

  return { tokenUsage, cost, toolCost };
}

/** Row shape for the `messages` table (general/agent chats). */
export function buildMessageRow(
  message: UIMessage,
  opts: { userId: string; chatId: string; model: string; sequenceNumber: number; agentId?: string | null }
) {
  message = cleanLiveRequestMessage(message);
  const { tokenUsage, cost, toolCost } = buildTokenUsageAndCost(message);

  return {
    chat_id: opts.chatId,
    user_id: opts.userId,
    content: getTextContent(message),
    role: message.role,
    model: message.role === "assistant" ? opts.model : "",
    image_paths: [],
    sequence_number: opts.sequenceNumber,
    parts: serializeParts(message),
    token_usage: tokenUsage,
    cost,
    tool_cost: toolCost,
    agent_id: opts.agentId ?? null,
    // True dedup key when both the client and the server may persist the
    // same turn — see 20260910000002_add_message_client_id_dedup.sql for why
    // sequence_number alone isn't sufficient.
    client_message_id: message.id,
  };
}

/** Row shape for the `project_messages` table (project chats). */
export function buildProjectMessageRow(
  message: UIMessage,
  opts: { userId: string; projectChatId: string; model: string; sequenceNumber: number; agentId?: string | null }
) {
  message = cleanLiveRequestMessage(message);
  const { tokenUsage, cost, toolCost } = buildTokenUsageAndCost(message);

  return {
    project_chat_id: opts.projectChatId,
    user_id: opts.userId,
    content: getTextContent(message),
    role: message.role,
    model: message.role === "assistant" ? opts.model : "",
    image_paths: [],
    sequence_number: opts.sequenceNumber,
    parts: serializeParts(message),
    token_usage: tokenUsage,
    cost,
    tool_cost: toolCost,
    agent_id: opts.agentId ?? null,
    client_message_id: message.id,
  };
}
